import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Alert, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { UserService } from '../UserService';
import {
    IAdvancedInvoiceBank,
    IAdvancedInvoiceContact,
    IAdvancedInvoiceData,
} from '../../../model/user/IAdvancedInvoiceData';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';
import ToggleChip from '../../../shared/components/ToggleChip';
import { getCountryName } from '../../../utils/helpers/CorrectCountryNames';
import { ErrorToast } from '../../../utils/toasters';

interface IUserAdvancedInvoiceTab {
    userId: number;
    isActive: boolean;
    onDirtyChange: (isDirty: boolean) => void;
}

export interface IUserAdvancedInvoiceTabHandle {
    /** Shows the field errors and returns whether the draft can be saved. */
    validate: () => boolean;
    save: () => Promise<void>;
}

type Draft = { contact: IAdvancedInvoiceContact; bank: IAdvancedInvoiceBank };

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VAT_REGEX = /^[a-zA-Z0-9]{8,16}$/;
const AUSTRIA = 'AT';

const toDraft = (data: IAdvancedInvoiceData): Draft => ({ contact: { ...data.contact }, bank: { ...data.bank } });

const sectionTitle = (title: string) => <h5 className="mb-4">{title}</h5>;

const readOnlyRow = (label: string, value?: string | boolean | null) => (
    <div className="d-flex justify-content-between gap-4 py-2 border-bottom">
        <span className="text-muted">{label}</span>
        <span className="text-end fw-semibold text-break">
            {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value || '-'}
        </span>
    </div>
);

const ReadOnlyInvoiceData = ({ contact, bank }: Draft) => (
    <Row>
        <Col lg={6}>
            {readOnlyRow('First name', contact.invoiceFirstName)}
            {readOnlyRow('Last name', contact.invoiceLastName)}
            {readOnlyRow('Company name', contact.companyName)}
            {readOnlyRow('Company register', contact.companyRegister)}
            {readOnlyRow('Phone', bank.accountPhone)}
            {readOnlyRow('Email', contact.email)}
            {readOnlyRow('VAT number', contact.uidNumber)}
        </Col>
        <Col lg={6}>
            {readOnlyRow('Street', contact.street)}
            {readOnlyRow('City', contact.city)}
            {readOnlyRow('Postal code', contact.zip)}
            {readOnlyRow('State', contact.province)}
            {readOnlyRow('Country', bank.accountCountry ? getCountryName(bank.accountCountry) : null)}
            {readOnlyRow('Bank name', bank.accountBankName)}
            {readOnlyRow('IBAN', bank.accountIban)}
            {readOnlyRow('SWIFT', bank.accountSwift)}
            {readOnlyRow('Small business owner', contact.smallBusiness)}
            {readOnlyRow('Sole proprietor', contact.soleProprietor)}
        </Col>
    </Row>
);

export const UserAdvancedInvoiceTab = forwardRef<IUserAdvancedInvoiceTabHandle, IUserAdvancedInvoiceTab>(
    function UserAdvancedInvoiceTab({ userId, isActive, onDirtyChange }, ref) {
        const { country } = useSelector((state: any) => state.enums);
        const [data, setData] = useState<IAdvancedInvoiceData | null>(null);
        const [draft, setDraft] = useState<Draft | null>(null);
        const [initialSnapshot, setInitialSnapshot] = useState('');
        const [isLoading, setIsLoading] = useState(false);
        const [showErrors, setShowErrors] = useState(false);

        const isDirty = draft !== null && JSON.stringify(draft) !== initialSnapshot;

        useEffect(() => {
            onDirtyChange(isDirty);
        }, [isDirty, onDirtyChange]);

        const applyData = useCallback((response: IAdvancedInvoiceData) => {
            const next = toDraft(response);
            setData(response);
            setDraft(next);
            setInitialSnapshot(JSON.stringify(next));
            setShowErrors(false);
        }, []);

        // Reload every time the tab is opened (the agency can change on the General tab), but never drop an unsaved draft.
        useEffect(() => {
            if (!isActive || isDirty) return;
            setIsLoading(true);
            UserService.getAdvancedInvoice(userId)
                .then(applyData)
                .catch(err => ErrorToast(err))
                .finally(() => setIsLoading(false));
        }, [isActive, userId, applyData]);

        const errors = useMemo(() => {
            const result: Record<string, string> = {};
            if (!draft) return result;
            const { contact, bank } = draft;
            const required: [string, string | null, string][] = [
                ['invoiceFirstName', contact.invoiceFirstName, 'First name is required'],
                ['invoiceLastName', contact.invoiceLastName, 'Last name is required'],
                ['companyName', contact.companyName, 'Company name is required'],
                ['accountPhone', bank.accountPhone, 'Phone is required'],
                ['street', contact.street, 'Street is required'],
                ['city', contact.city, 'City is required'],
                ['zip', contact.zip, 'Postal code is required'],
                ['province', contact.province, 'State is required'],
                ['accountCountry', bank.accountCountry, 'Country is required'],
                ['accountBankName', bank.accountBankName, 'Bank name is required'],
                ['accountIban', bank.accountIban, 'IBAN is required'],
            ];
            required.forEach(([key, value, message]) => {
                if (!value?.trim()) result[key] = message;
            });
            if (!contact.email?.trim()) {
                result.email = 'Email is required';
            } else if (!EMAIL_REGEX.test(contact.email.trim())) {
                result.email = 'Enter a valid email';
            }
            const vat = contact.uidNumber?.trim() ?? '';
            if (vat && !VAT_REGEX.test(vat)) {
                result.uidNumber = 'VAT number must be 8-16 letters or digits, without spaces';
            } else if (!vat && !contact.smallBusiness && !contact.soleProprietor) {
                result.uidNumber = 'VAT number is required unless small business owner or sole proprietor';
            }
            return result;
        }, [draft]);

        const setContact = (field: keyof IAdvancedInvoiceContact, value: string | boolean) =>
            setDraft(prev => (prev ? { ...prev, contact: { ...prev.contact, [field]: value } } : prev));

        const setBank = (field: keyof IAdvancedInvoiceBank, value: string) =>
            setDraft(prev => {
                if (!prev) return prev;
                const next = { ...prev, bank: { ...prev.bank, [field]: value } };
                // Small business status only exists for Austria, same as on the web.
                if (field === 'accountCountry' && value !== AUSTRIA) {
                    next.contact = { ...prev.contact, smallBusiness: false };
                }
                return next;
            });

        const text = (
            label: string,
            key: string,
            value: string | null,
            onChange: (value: string) => void,
            md = 6,
            required = true
        ) => (
            <Form.Group as={Col} md={md} className="mb-3">
                <Form.Label>
                    {label}
                    {required && <span className="text-danger"> *</span>}
                </Form.Label>
                <Form.Control
                    value={value ?? ''}
                    onChange={event => onChange(event.target.value)}
                    isInvalid={showErrors && !!errors[key]}
                />
                <Form.Control.Feedback type="invalid">{errors[key]}</Form.Control.Feedback>
            </Form.Group>
        );

        useImperativeHandle(ref, () => ({
            validate: () => {
                setShowErrors(true);
                return Object.keys(errors).length === 0;
            },
            save: async () => {
                if (!draft) return;
                const response = await UserService.saveAdvancedInvoice(userId, draft);
                applyData(response);
            },
        }));

        if ((isLoading && !data) || !draft || !data) {
            return (
                <div className="text-center py-10">
                    <Spinner animation="border" />
                </div>
            );
        }

        if (!data.isEditable && data.agency) {
            const hasOwnData = Object.values(data.contact).some(Boolean) || Object.values(data.bank).some(Boolean);
            return (
                <>
                    <Alert variant="info" className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <span>
                            This influencer is invoiced through <strong>{data.agency.displayName}</strong>. Offers and
                            contracts use the agency&apos;s invoice data, so the influencer&apos;s own data cannot be
                            edited here.
                        </span>
                    </Alert>
                    <Card className="mb-4">
                        <Card.Body>
                            {sectionTitle(`Invoice data used (${data.agency.displayName})`)}
                            <ReadOnlyInvoiceData contact={data.agency.contact} bank={data.agency.bank} />
                        </Card.Body>
                    </Card>
                    {hasOwnData && (
                        <Card className="mb-4">
                            <Card.Body>
                                {sectionTitle("Influencer's own data (not used while in an agency)")}
                                <ReadOnlyInvoiceData contact={data.contact} bank={data.bank} />
                            </Card.Body>
                        </Card>
                    )}
                </>
            );
        }

        const { contact, bank } = draft;

        return (
            <>
                <Card className="mb-4">
                    <Card.Body className="py-3">
                        This influencer is not in an agency, so offers, contracts and invoices use the data below.
                    </Card.Body>
                </Card>
                <Row className="g-4 mb-4">
                    <Col xl={6}>
                        <Card className="h-100">
                            <Card.Body>
                                {sectionTitle('Receiver')}
                                <Row>
                                    {text('First name', 'invoiceFirstName', contact.invoiceFirstName, value =>
                                        setContact('invoiceFirstName', value)
                                    )}
                                    {text('Last name', 'invoiceLastName', contact.invoiceLastName, value =>
                                        setContact('invoiceLastName', value)
                                    )}
                                    {text(
                                        'Company name',
                                        'companyName',
                                        contact.companyName,
                                        value => setContact('companyName', value),
                                        12
                                    )}
                                    {text(
                                        'Company register',
                                        'companyRegister',
                                        contact.companyRegister,
                                        value => setContact('companyRegister', value),
                                        12,
                                        false
                                    )}
                                    {text('Phone', 'accountPhone', bank.accountPhone, value =>
                                        setBank('accountPhone', value)
                                    )}
                                    {text('Email', 'email', contact.email, value => setContact('email', value))}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={6}>
                        <Card className="h-100">
                            <Card.Body>
                                {sectionTitle('Address')}
                                <Row>
                                    {text('Street', 'street', contact.street, value => setContact('street', value), 12)}
                                    {text('City', 'city', contact.city, value => setContact('city', value))}
                                    {text('Postal code', 'zip', contact.zip, value => setContact('zip', value))}
                                    <Form.Group as={Col} md={6} className="mb-3">
                                        <Form.Label>
                                            Country<span className="text-danger"> *</span>
                                        </Form.Label>
                                        <SingleSelectDropdown
                                            placeholder="Select country"
                                            withImage
                                            clearable={false}
                                            options={(country ?? []).map((code: string) => ({
                                                value: code,
                                                label: getCountryName(code),
                                                image: `/flags/4x3/${code.toLowerCase()}.svg`,
                                            }))}
                                            selected={bank.accountCountry ?? ''}
                                            onChange={value => setBank('accountCountry', value)}
                                        />
                                        {showErrors && errors.accountCountry && (
                                            <div className="text-danger fs-8 mt-1">{errors.accountCountry}</div>
                                        )}
                                    </Form.Group>
                                    {text('State', 'province', contact.province, value =>
                                        setContact('province', value)
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <Row className="g-4 mb-4">
                    <Col xl={6}>
                        <Card className="h-100">
                            <Card.Body>
                                {sectionTitle('Payment')}
                                <Row>
                                    {text('Bank name', 'accountBankName', bank.accountBankName, value =>
                                        setBank('accountBankName', value)
                                    )}
                                    {text('IBAN', 'accountIban', bank.accountIban, value =>
                                        setBank('accountIban', value)
                                    )}
                                    {text(
                                        'SWIFT',
                                        'accountSwift',
                                        bank.accountSwift,
                                        value => setBank('accountSwift', value),
                                        6,
                                        false
                                    )}
                                    {text(
                                        'VAT number',
                                        'uidNumber',
                                        contact.uidNumber,
                                        value => setContact('uidNumber', value),
                                        6,
                                        !contact.smallBusiness && !contact.soleProprietor
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={6}>
                        <Card className="h-100">
                            <Card.Body>
                                {sectionTitle('Billing details')}
                                <div className="d-flex flex-column gap-4">
                                    <div>
                                        <ToggleChip
                                            label="Sole proprietor"
                                            checked={contact.soleProprietor}
                                            onChange={event => setContact('soleProprietor', event.target.checked)}
                                        />
                                        <div className="fs-8 text-muted mt-2">
                                            Sole proprietors can be invoiced without a VAT number.
                                        </div>
                                    </div>
                                    {bank.accountCountry === AUSTRIA && (
                                        <div>
                                            <ToggleChip
                                                label="Small business owner"
                                                checked={contact.smallBusiness}
                                                onChange={event => setContact('smallBusiness', event.target.checked)}
                                            />
                                            <div className="fs-8 text-muted mt-2">
                                                Austrian small business regulation (Kleinunternehmer). Only available
                                                for Austria.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </>
        );
    }
);
